/**
 * Borrower Experience Workbook — `/experience/*`
 * (frontend_documentation/experience-workbook.md, eastview-os `app/routers/experience.py`).
 *
 * The track record as line items: each `ExperienceItem` is a property the
 * borrower / entity / GC did (`ValidatedExperience` fields + the schedule's
 * economics), carrying PROOF documents and PARENT parties in `associations`,
 * a server-owned `verified` flag, and `countedForExperience`. Verifying is the
 * gate that (a) rolls the count onto the loan and (b) persists the item to the
 * cross-LOS canonical person / entity / GC record.
 *
 * CONTRACT: every list-mutating call returns the FULL updated `items[]` —
 * replace local state, never merge. `verified` / `verifiedBy` / `verifiedDate`
 * / `createdAt` / `createdBy` are stripped from create + update bodies; only
 * `/verify` moves verification. `profitEstimate` on a row is computed at
 * serialization (never stored) — render it, don't recompute it.
 *
 * Conditional: RTL-family + Ground Up Construction only. Read
 * `experienceApplies` off `/check` (or `/items`); `POST /items` 409s on a
 * non-applicable loan. `/check` also carries `experienceChatEnabled` — the
 * chat endpoints are limited to the "AI Review Visibility" org group and
 * must only be called when that flag is true.
 */
import { losHttp, LOS_API_BASE } from "../lib/losHttp";
import { getAuthToken } from "../lib/digitalSizerHttp";

const PREFIX = "/experience";
const list = (data) => (Array.isArray(data) ? data : data?.items || []);

export const experienceApi = {
  /** GET /experience/check/{dealId} → `{ items[], totalVerifiedExperience, experienceApplies, experienceChatEnabled }`. */
  check: (dealId) => losHttp.get(`${PREFIX}/check/${dealId}`).then((r) => r.data),

  /** GET /experience/items/{dealId} → `{ experienceApplies, items[] }` (rows carry `profitEstimate`). */
  items: (dealId) => losHttp.get(`${PREFIX}/items/${dealId}`).then((r) => r.data),

  /** POST /experience/items/{dealId} — create; body may include `associations`. 409 when not applicable. → items[] */
  create: (dealId, body) =>
    losHttp.post(`${PREFIX}/items/${dealId}`, body).then((r) => list(r.data)),

  /** POST …/{itemId}/update — partial update (server-owned fields ignored). → items[] */
  update: (dealId, itemId, body) =>
    losHttp.post(`${PREFIX}/items/${dealId}/${itemId}/update`, body).then((r) => list(r.data)),

  /**
   * POST …/{itemId}/verify — `{ verified }` (default true). → `{ items[], persistence }`.
   * `persistence` is `{persons[], entities[], queued[]}` | `{skipped}` | `{error}` | `{withdrawnFrom}`.
   */
  verify: (dealId, itemId, verified = true) =>
    losHttp
      .post(`${PREFIX}/items/${dealId}/${itemId}/verify`, { verified })
      .then((r) => r.data || {}),

  /** POST …/associations/{type}/{id}/add|remove → `{ item, items[] }`. type ∈ borrowers·entities·generalContractors·loanDocuments·subjectProperties */
  addAssociation: (dealId, itemId, type, id) =>
    losHttp
      .post(`${PREFIX}/items/${dealId}/${itemId}/associations/${type}/${id}/add`)
      .then((r) => r.data || {}),
  removeAssociation: (dealId, itemId, type, id) =>
    losHttp
      .post(`${PREFIX}/items/${dealId}/${itemId}/associations/${type}/${id}/remove`)
      .then((r) => r.data || {}),

  /** DELETE …/{itemId} → items[] */
  remove: (dealId, itemId) =>
    losHttp.delete(`${PREFIX}/items/${dealId}/${itemId}`).then((r) => list(r.data)),

  /** POST /experience/populate_from_extracts/{dealId} → `{ added, skippedExisting, candidatesFound, items[] }` */
  populate: (dealId) =>
    losHttp.post(`${PREFIX}/populate_from_extracts/${dealId}`).then((r) => r.data || {}),

  /** GET /experience/related_parties/{dealId}?max_hops=2 → `{ dealId, hops, relatedDealIds[], parties[] }` */
  relatedParties: (dealId, maxHops = 2) =>
    losHttp
      .get(`${PREFIX}/related_parties/${dealId}`, { params: { max_hops: maxHops } })
      .then((r) => r.data || {}),

  /**
   * POST /experience/extract_document/{dealId}/{documentId} — `{ context: "borrower"|"contractor" }`.
   * Runs the REAL track-record extractor on one document, then seeds candidates.
   * → `{ extraction: {ok, rejected, error, extractor, documentName, originalDocType, ranAsDocType, reclassification},
   *      added, skippedExisting, candidatesFound, items[] }`. Long-running (LLM).
   */
  extractDocument: (dealId, documentId, context = "borrower") =>
    losHttp
      .post(`${PREFIX}/extract_document/${dealId}/${documentId}`, { context }, { timeout: 0 })
      .then((r) => r.data || {}),

  /** GET /experience/document_extract/{dealId}/{documentId} → read-only extract pane (`exists: false` = never parsed). */
  documentExtract: (dealId, documentId) =>
    losHttp.get(`${PREFIX}/document_extract/${dealId}/${documentId}`).then((r) => r.data || {}),

  /** GET /experience/chat/{dealId} → `{ dealId, messages[], shared, assistant }`. 403 outside the org group. */
  chat: (dealId) => losHttp.get(`${PREFIX}/chat/${dealId}`).then((r) => r.data || {}),

  /**
   * POST /experience/chat/{dealId} → text/event-stream of AG-UI frames
   * (`data: {json}\n\n`). `payload` is the message string, or the composer's
   * object `{ message, attachments?: [{ loanDocumentId, fileName, docType }],
   * images?: [{ fileName, mimeType, dataBase64 }] }` — the server reads
   * `message` today (text ≤ 8000 chars; the composer folds attached document
   * names into it) and ignores the rest until it grows vision / attachment
   * support. Streams via fetch (axios can't read a body incrementally) with the
   * same bearer the interceptor attaches. `onFrame` receives each parsed frame;
   * resolves when the stream ends. 403 → the caller hides the panel; 413 →
   * message too long.
   */
  streamChatTurn: async (dealId, payload, { onFrame, signal } = {}) => {
    const token = getAuthToken();
    const res = await fetch(`${LOS_API_BASE}${PREFIX}/chat/${dealId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(typeof payload === "string" ? { message: payload } : payload),
      signal,
    });
    if (!res.ok) {
      let detail = null;
      try {
        detail = await res.json();
      } catch (_) {
        /* not json */
      }
      const err = new Error(detail?.error || detail?.detail?.error || `HTTP ${res.status}`);
      err.status = res.status;
      throw err;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep = buffer.indexOf("\n\n");
      while (sep !== -1) {
        const chunk = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        chunk.split("\n").forEach((line) => {
          const m = /^data:\s?(.*)$/.exec(line);
          if (!m) return;
          try {
            onFrame?.(JSON.parse(m[1]));
          } catch (_) {
            /* keep-alive or non-json line */
          }
        });
        sep = buffer.indexOf("\n\n");
      }
    }
  },
};

export default experienceApi;
